/**
 * JobPayPanel — the office's side of FIELD-PAY-01 (Seth approved the mock
 * 2026-09-24), top of the job's Money & Close-out tab:
 *
 *   · How They're Paying — pay in full / financing / bill after the install.
 *     Seeded from the sale (the customer's choice at acceptance); the office
 *     changes it here and the installer's Balance card follows.
 *   · Financing leads with the PLAN TO PROCESS (Seth: "which specific financing
 *     plan was selected so office knows which plan to process") — name, rate,
 *     term and the lender's plan number — then Applied → Approved → Funded.
 *     Funded records the lender's money as a payment held for the job.
 *   · Payments — everything paid on this job (the deposit, the field, the
 *     lender), with cash the office marks received.
 * Everything held for the job lands on the final invoice at close (close_job v2).
 */
import { useEffect, useMemo, useState } from "react";
import { Banknote, Check, Loader2, Wallet } from "lucide-react";
import { cn } from "@/lib/cn";
import { Button } from "@/components/ui/Button";
import { InfoTip } from "@/components/InfoTip";
import { CopyCode } from "@/components/Copyable";
import { useMemberName } from "@/components/TeamMemberSelect";
import { useJobMoney } from "./installField";
import {
  useJobPayments, useSetPayPlan, useFinancingPlanList, useMarkCashReceived, methodLabel, COUNTED, type JobPayment,
} from "./fieldPay";
import type { Job } from "./jobs";

const money = (n: number) => n.toLocaleString("en-US", { style: "currency", currency: "USD" });
const when = (iso: string | null | undefined) =>
  iso ? new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" }) : "";
const PLAN_LABEL = { full: "Pay in Full", financing: "Financing", bill_after: "Bill After the Install" } as const;

export function JobPayPanel({ job, closed }: { job: Job; closed: boolean }) {
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <HowTheyrePaying job={job} closed={closed} />
      <JobPaymentsCard job={job} />
    </div>
  );
}

function HowTheyrePaying({ job, closed }: { job: Job; closed: boolean }) {
  const { data: m } = useJobMoney(job.id);
  const { data: plans } = useFinancingPlanList();
  const set = useSetPayPlan(job.id);
  const plan = m?.pay_plan ?? job.pay_plan ?? "full";
  const status = m?.fin_status ?? job.fin_status ?? null;
  const [planId, setPlanId] = useState<string | null>(job.fin_plan_id ?? null);
  const [approval, setApproval] = useState(job.fin_approval_no ?? "");
  const [amountText, setAmountText] = useState(job.fin_amount != null ? String(job.fin_amount) : "");
  useEffect(() => { setPlanId(job.fin_plan_id ?? null); }, [job.fin_plan_id]);
  useEffect(() => { setApproval(job.fin_approval_no ?? ""); }, [job.fin_approval_no]);
  useEffect(() => { setAmountText(job.fin_amount != null ? String(job.fin_amount) : ""); }, [job.fin_amount]);
  const chosen = useMemo(() => (plans ?? []).find((p) => p.id === planId) ?? null, [plans, planId]);
  const amount = amountText.trim() === "" ? null : Number(amountText);
  const amountOk = amount != null && Number.isFinite(amount) && amount > 0;
  const fromSale = job.fin_updated_by === "the sale";
  const save = (v: Parameters<typeof set.mutate>[0]) => set.mutate(v);
  const fin = (st: string) => save({ pay_plan: "financing", plan_id: planId, fin_status: st, approval_no: approval, amount: amountOk ? amount : null });

  const installerSees = !m ? "" : m.collect === "paid" ? "Paid in full — nothing to collect"
    : m.collect === "financing_pending" ? "Financing not approved yet — don't collect"
    : m.collect === "bill_after" ? "The office bills after the install"
    : m.collect === "closed" ? "Job closed"
    : `Collect ${money(m.due)}`;

  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-3 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
        <Wallet className="h-[18px] w-[18px] text-brand-500" /> How They&apos;re Paying
        <InfoTip label="Where this comes from">Set by the sale — what the customer chose when they accepted the proposal. Change it here and the installer&apos;s Balance card follows.</InfoTip>
        <span className="ml-auto text-xs font-medium text-slate-400">
          {fromSale ? "From the sale" : job.fin_updated_by ? `${job.fin_updated_by.split("@")[0]} · ${when(job.fin_updated_at)}` : ""}
        </span>
      </h3>

      <div className="inline-flex flex-wrap gap-0.5 rounded-lg bg-slate-100 p-0.5">
        {(["full", "financing", "bill_after"] as const).map((k) => (
          <button key={k} type="button"
            // Funded = the lender's money is in: undo Funded before changing how they pay.
            disabled={closed || set.isPending || (plan === "financing" && status === "funded" && k !== "financing")}
            title={plan === "financing" && status === "funded" && k !== "financing" ? "Undo Funded first" : undefined}
            onClick={() => k === plan ? undefined : save(k === "financing"
              ? { pay_plan: "financing", plan_id: planId, fin_status: status && status !== "declined" ? status : "applied", approval_no: approval, amount: amountOk ? amount : null }
              : { pay_plan: k, fin_status: plan === "financing" && status !== "funded" ? null : status })}
            className={cn("rounded-md px-3.5 py-2 text-[13px] font-semibold transition-colors disabled:opacity-60",
              plan === k ? "bg-white text-brand-700 shadow-sm" : "text-slate-600 hover:text-slate-900")}>
            {PLAN_LABEL[k]}
          </button>
        ))}
      </div>

      {plan === "financing" && (
        <div className="mt-4 space-y-4">
          <div className="flex flex-wrap items-center gap-4 rounded-xl bg-brand-50/70 px-4 py-3.5 ring-1 ring-brand-100">
            <div className="min-w-0 flex-1">
              <div className="text-[11px] font-bold uppercase tracking-[0.1em] text-brand-700">Plan to process</div>
              <div className="mt-0.5 text-lg font-bold text-slate-900">{chosen?.name ?? m?.fin_plan_name ?? "No plan picked"}</div>
              {chosen && (
                <div className="text-[13px] text-slate-600">
                  Wells Fargo · {Number(chosen.apr ?? 0)}% APR · {chosen.term_months} months{chosen.is_active ? "" : " · no longer offered"}
                </div>
              )}
            </div>
            <div className="text-right">
              <div className="text-[11px] font-semibold text-slate-500">Plan number</div>
              <div className="text-2xl font-extrabold tracking-wide text-slate-900">
                {chosen?.plan_number ? <CopyCode value={chosen.plan_number} label="Copy plan number" variant="text" size="lg" className="font-extrabold text-slate-900" /> : "—"}
              </div>
            </div>
            <label className="block w-full sm:w-auto">
              <span className="sr-only">Change plan</span>
              <select value={planId ?? ""} disabled={closed || set.isPending || status === "funded"}
                onChange={(e) => { const v = e.target.value || null; setPlanId(v); save({ pay_plan: "financing", plan_id: v, fin_status: status ?? "applied", approval_no: approval, amount: amountOk ? amount : null }); }}
                className="jinp w-full bg-white sm:w-56">
                <option value="">Change plan…</option>
                {(plans ?? []).filter((p) => p.is_active || p.id === planId).map((p) => (
                  <option key={p.id} value={p.id}>{p.name}{p.plan_number ? ` · ${p.plan_number}` : ""}</option>
                ))}
              </select>
            </label>
          </div>

          <ol className="flex gap-3">
            {(["applied", "approved", "funded"] as const).map((st, i) => {
              const order = { applied: 0, approved: 1, funded: 2, declined: -1 } as const;
              const cur = status ? order[status] : 0;
              const done = cur > i;
              const now = cur === i;
              return (
                <li key={st} className="flex flex-1 items-center gap-2">
                  <span className={cn("flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-[12px] font-bold",
                    done ? "bg-emerald-600 text-white" : now ? "bg-brand-700 text-white" : "bg-slate-100 text-slate-500")}>
                    {done ? <Check className="h-3.5 w-3.5" strokeWidth={3} /> : i + 1}
                  </span>
                  <span className="text-[13px] font-semibold text-slate-700">{st === "applied" ? "Applied" : st === "approved" ? "Approved" : "Funded"}</span>
                </li>
              );
            })}
          </ol>

          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block">
              <span className="text-xs font-semibold text-slate-500">Approval number</span>
              <input value={approval} onChange={(e) => setApproval(e.target.value)} disabled={closed}
                onBlur={() => approval !== (job.fin_approval_no ?? "") && save({ pay_plan: "financing", plan_id: planId, fin_status: status ?? "applied", approval_no: approval, amount: amountOk ? amount : null })}
                className="jinp mt-1 w-full font-mono" />
            </label>
            <label className="block">
              <span className="text-xs font-semibold text-slate-500">Approved amount</span>
              <div className="relative mt-1">
                <span className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-sm text-slate-400">$</span>
                <input type="number" inputMode="decimal" value={amountText} onChange={(e) => setAmountText(e.target.value)} disabled={closed}
                  onBlur={() => amountText !== (job.fin_amount != null ? String(job.fin_amount) : "") && (amountOk || amountText === "") && save({ pay_plan: "financing", plan_id: planId, fin_status: status ?? "applied", approval_no: approval, amount })}
                  className="jinp w-full !pl-6 text-right tabular-nums" />
              </div>
            </label>
          </div>

          {!closed && (
            <div className="flex flex-wrap items-center gap-2">
              {(status === "applied" || !status) && (
                <Button size="sm" disabled={!amountOk || set.isPending} onClick={() => fin("approved")}>Mark Approved</Button>
              )}
              {status === "approved" && (
                <>
                  <Button size="sm" disabled={!amountOk || set.isPending} onClick={() => fin("funded")}>Mark Funded</Button>
                  <Button size="sm" variant="ghost" disabled={set.isPending} onClick={() => fin("applied")}>Back to Applied</Button>
                </>
              )}
              {status === "funded" && (
                <Button size="sm" variant="ghost" disabled={set.isPending} onClick={() => fin("approved")}>Undo Funded</Button>
              )}
              {status !== "funded" && (
                <Button size="sm" variant="secondary" disabled={set.isPending}
                  onClick={() => save({ pay_plan: "full", fin_status: "declined" })}>Declined — Switch to Pay in Full</Button>
              )}
              {set.isPending && <Loader2 className="h-4 w-4 animate-spin text-slate-400" />}
              {!amountOk && (status === "applied" || status === "approved" || !status) && <span className="text-xs text-slate-400">Enter the approved amount to move it forward.</span>}
            </div>
          )}
        </div>
      )}

      {plan !== "financing" && status === "declined" && (
        <p className="mt-3 text-[13px] text-slate-500">Financing was declined{job.fin_updated_at ? ` (${when(job.fin_updated_at)})` : ""}.</p>
      )}

      {set.isError && <p className="mt-3 text-[13px] font-medium text-rose-600">{(set.error as Error).message}</p>}

      {m && !closed && (
        <div className="mt-4 flex items-center gap-2 border-t border-hairline pt-3 text-[13px] text-slate-500">
          The installer sees: <span className="font-semibold text-slate-800">{installerSees}</span>
        </div>
      )}
    </section>
  );
}

function JobPaymentsCard({ job }: { job: Job }) {
  const { data: m } = useJobMoney(job.id);
  const { data: pays, isLoading } = useJobPayments(job.id);
  const received = useMarkCashReceived();
  const memberName = useMemberName();
  const rows = (pays ?? []).filter((p) => COUNTED.has(p.status) || p.status === "pending");
  return (
    <section className="rounded-xl border border-hairline bg-white p-4">
      <h3 className="mb-3 flex flex-wrap items-center gap-2 text-base font-semibold text-slate-800">
        <Banknote className="h-[18px] w-[18px] text-brand-500" /> Payments
        <InfoTip label="Where these go">Payments taken in the field and lender funding are held for this job. When you close the job they land on the final invoice, with the deposit.</InfoTip>
      </h3>
      {isLoading ? (
        <div className="h-16 animate-pulse rounded-lg bg-slate-100" />
      ) : rows.length === 0 ? (
        <p className="text-sm text-slate-400">No payments on this job yet.</p>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] font-semibold uppercase tracking-wide text-slate-400">
              <th className="pb-1.5 font-semibold">When</th><th className="pb-1.5 font-semibold">How</th><th className="pb-1.5 font-semibold">By</th>
              <th className="pb-1.5 text-right font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-hairline">
            {rows.map((p) => (
              <tr key={p.id} className="align-top">
                <td className="py-2 pr-2 text-slate-600">{when(p.created_at)}</td>
                <td className="py-2 pr-2">
                  <span className="font-medium text-slate-800">{p.from_deposit ? "Deposit · " : ""}{methodLabel(p)}</span>
                  <PayStatus p={p} />
                  {p.method === "cash" && p.source === "field" && (
                    <span className="mt-1 block text-[12px]">
                      {p.cash_turned_in_at ? (
                        <span className="text-emerald-700">Received {when(p.cash_turned_in_at)}</span>
                      ) : (
                        <>
                          <span className="text-amber-700">{p.cash_dropped_at ? `Crew dropped it off ${when(p.cash_dropped_at)}` : "Still with the crew"}</span>
                          <button type="button" disabled={received.isPending} onClick={() => received.mutate({ paymentId: p.id })}
                            className="ml-2 font-semibold text-brand-700 hover:underline">Received It</button>
                        </>
                      )}
                    </span>
                  )}
                </td>
                <td className="py-2 pr-2 text-slate-600">{p.source === "lender" ? "Lender" : p.source === "portal" ? "Customer" : memberName(p.collected_by_empno) ?? (p.created_by ?? "").split("@")[0]}</td>
                <td className="py-2 text-right font-semibold tabular-nums text-slate-900">{money(p.amount)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {m && (
        <div className="mt-3 space-y-1 border-t border-hairline pt-3 text-sm">
          <div className="flex justify-between text-slate-500"><span>Job total</span><span className="tabular-nums">{money(m.total)}</span></div>
          <div className="flex justify-between text-slate-500"><span>Paid</span><span className="tabular-nums text-emerald-700">− {money(m.paid)}</span></div>
          {m.financed > 0 && <div className="flex justify-between text-slate-500"><span>Financing approved (not funded yet)</span><span className="tabular-nums">− {money(m.financed)}</span></div>}
          <div className="flex justify-between font-semibold text-slate-900"><span>Still owed</span><span className="tabular-nums">{money(m.due)}</span></div>
        </div>
      )}
      {received.isError && <p className="mt-2 text-[13px] font-medium text-rose-600">{(received.error as Error).message}</p>}
    </section>
  );
}

function PayStatus({ p }: { p: JobPayment }) {
  if (p.status === "succeeded" || p.status === "recorded") return null;
  const label = p.status === "pending" ? "waiting" : p.status === "processing" ? "processing" : p.status.replace(/_/g, " ");
  return <span className="ml-1.5 rounded-full bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-500">{label}</span>;
}
